import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Field, Badge } from "@/components/ui-hud";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AirportInput from "@/components/AirportInput";
import RoutingInput from "@/components/RoutingInput";
import { Plane, Users2, Calendar, ArrowLeft, ArrowRight, Loader2, CheckCircle2, X, Globe2 } from "lucide-react";

const STEPS = ["Route", "Passengers & Dates", "Aircraft & Budget", "Extras", "Contact", "Review"];
const CLASSES = ["Any", "Very light", "Light", "Midsize", "Super-mid", "Large-cabin", "Ultra-long-range", "Turboprop"];

export default function CharterRequest() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preferredOperatorId = params.get("operator_id") || "";
  const preferredAircraftId = params.get("aircraft_id") || "";
  const preferredTail = params.get("tail") || "";
  const preferredType = params.get("type") || "";
  const preferredOperatorName = params.get("company") || "";

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    departure_icao: "", destination_icao: "", routing: [],
    passengers: 2, trip_start: "", trip_end: "",
    aircraft_class_pref: "Any", budget_usd: "",
    catering: "", pets: false, special_requests: "",
    contact_name: "", contact_email: "", contact_phone: "",
  });

  const started = useRef(Date.now());
  const [elapsed, setElapsed] = useState("00:00");
  useEffect(() => {
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - started.current) / 1000);
      setElapsed(`${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const next = () => {
    if (step === 0 && (!form.departure_icao || !form.destination_icao)) return toast.error("Both airports required");
    if (step === 1 && (!form.trip_start || !form.passengers)) return toast.error("Passengers + trip start required");
    if (step === 4 && (!form.contact_name || !form.contact_email)) return toast.error("Name + email required");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/charter-requests", {
        ...form,
        passengers: Number(form.passengers) || 1,
        budget_usd: form.budget_usd ? Number(form.budget_usd) : null,
        preferred_operator_id: preferredOperatorId || null,
        preferred_aircraft_id: preferredAircraftId || null,
        preferred_tail_number: preferredTail || null,
      });
      setResult(r.data);
      const routedTo = preferredOperatorId
        ? `${preferredOperatorName || "the operator"}${preferredTail ? ` · ${preferredTail}` : ""}`
        : `${r.data.matched_operators} operators`;
      toast.success(`Sent to ${routedTo}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] bg-black/60 py-10 px-4 flex items-start md:items-center justify-center">
      <Card testId="charter-request-modal" className="w-full max-w-2xl relative border border-sky/40" style={{boxShadow:"0 0 42px rgba(75,156,255,0.18)"}}>
        <button onClick={() => nav(-1)} data-testid="cq-close" className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5 text-white/50"><X className="h-4 w-4" /></button>

        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md mono text-[11px] font-bold tracking-wider" style={{background:"rgba(75,156,255,0.14)", color:"#4B9CFF", border:"1px solid rgba(75,156,255,0.5)"}}>
              <Plane className="h-3 w-3" /> CHARTER
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Request a Quote</h2>
          </div>
          <div className="hidden md:block mono text-[11px] text-white/40 tracking-widest">{elapsed}</div>
        </div>

        <div className="mt-3 mb-1">
          <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-sky transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>
        <div className="mono text-[11px] text-white/50 mb-5">Step {step + 1} of {STEPS.length} — <span className="text-white/80">{STEPS[step]}</span></div>

        {/* Locked-target banner when this request is deep-linked from an aircraft on OperatorDetail. */}
        {preferredOperatorId && (
          <div className="mb-4 rounded-lg border border-cyan/40 bg-cyan/[0.06] px-3 py-2 flex items-center gap-3" data-testid="cq-target-lock">
            <Plane className="h-4 w-4 text-cyan" />
            <div className="flex-1">
              <div className="mono text-[10px] tracking-widest text-cyan">// REQUESTING THIS SPECIFIC AIRCRAFT</div>
              <div className="text-sm text-white/90">
                {preferredTail && <span className="mono text-cyan font-bold">{preferredTail}</span>}
                {preferredType && <span className="mono text-white/60 ml-1">· {preferredType}</span>}
                {preferredOperatorName && <span className="text-white/70 ml-2">operated by {preferredOperatorName}</span>}
              </div>
            </div>
            <button type="button" onClick={() => nav(-1)} className="text-[10px] mono text-white/50 hover:text-white underline underline-offset-4">CHANGE</button>
          </div>
        )}

        {result ? (
          <SentConfirmation result={result} form={form} onDone={() => nav(`/charter-requests/${result.request_id}`)} />
        ) : (
          <>
            {step === 0 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Departure *">
                    <AirportInput testId="cq-departure" value={form.departure_icao} onChange={(v) => setForm({...form, departure_icao: v})} />
                  </Field>
                  <Field label="Destination *">
                    <AirportInput testId="cq-destination" value={form.destination_icao} onChange={(v) => setForm({...form, destination_icao: v})} />
                  </Field>
                </div>
                <Field label={<span>Multi-leg route <span className="text-white/40 text-xs">(optional)</span></span>}>
                  <RoutingInput testId="cq-routing" value={form.routing} onChange={(v) => setForm({...form, routing: v})} />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <Field label="Passengers *">
                  <input data-testid="cq-pax" type="number" min={1} inputMode="numeric" className="input" value={form.passengers} onChange={(e) => setForm({...form, passengers: e.target.value})} />
                </Field>
                <DateTimeHourAmPm
                  testId="cq-start"
                  label="Trip Start *"
                  value={form.trip_start}
                  onChange={(v) => setForm({ ...form, trip_start: v })}
                />
                <DateTimeHourAmPm
                  testId="cq-end"
                  label="Trip End (optional)"
                  value={form.trip_end}
                  onChange={(v) => setForm({ ...form, trip_end: v })}
                  minDate={form.trip_start ? form.trip_start.slice(0, 10) : undefined}
                />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Field label="Aircraft Class Preference">
                  <div className="grid grid-cols-4 gap-2">
                    {CLASSES.map((c) => (
                      <button key={c} type="button" data-testid={`cq-class-${c}`} onClick={() => setForm({...form, aircraft_class_pref: c})}
                        className={`px-3 py-2 rounded-md mono text-[11px] border ${form.aircraft_class_pref === c ? "border-sky text-sky bg-sky/10" : "border-white/10 text-white/70 hover:border-white/30"}`}>{c}</button>
                    ))}
                  </div>
                </Field>
                <Field label="Budget (USD)">
                  <input data-testid="cq-budget" type="number" className="input mono" value={form.budget_usd} onChange={(e) => setForm({...form, budget_usd: e.target.value})} placeholder="45000" />
                  <div className="mt-1 text-[11px] text-white/45">Optional. Helps operators calibrate their bids.</div>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Field label="Catering">
                  <input data-testid="cq-catering" className="input" value={form.catering} onChange={(e) => setForm({...form, catering: e.target.value})} placeholder="e.g. Kosher · vegetarian · Nobu Miami" />
                </Field>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input type="checkbox" data-testid="cq-pets" checked={form.pets} onChange={(e) => setForm({...form, pets: e.target.checked})} />
                  Pets on board
                </label>
                <Field label="Special Requests">
                  <textarea data-testid="cq-notes" rows={4} className="input" value={form.special_requests} onChange={(e) => setForm({...form, special_requests: e.target.value})} placeholder="Ground transport, medical equipment, WiFi requirements, etc." />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <Field label="Your Name *">
                  <input data-testid="cq-name" required className="input" value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} />
                </Field>
                <Field label="Email *">
                  <input data-testid="cq-email" required type="email" className="input" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} />
                </Field>
                <Field label="Phone">
                  <input data-testid="cq-phone" className="input mono" value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} />
                </Field>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-2 mono text-sm">
                <Row k="Route" v={form.routing.length ? form.routing.join(" → ") : `${form.departure_icao} → ${form.destination_icao}`} />
                <Row k="Passengers" v={form.passengers} />
                <Row k="Start" v={form.trip_start} />
                {form.trip_end && <Row k="End" v={form.trip_end} />}
                <Row k="Class" v={form.aircraft_class_pref} />
                {form.budget_usd && <Row k="Budget" v={`$${Number(form.budget_usd).toLocaleString()}`} />}
                {form.catering && <Row k="Catering" v={form.catering} />}
                {form.pets && <Row k="Pets" v="Yes" />}
                {form.special_requests && <Row k="Notes" v={form.special_requests} />}
                <Row k="Contact" v={`${form.contact_name} · ${form.contact_email}`} />
              </div>
            )}

            <div className="flex justify-between mt-8 pt-5 border-t border-white/[0.06]">
              {step > 0 ? (
                <button onClick={back} data-testid="cq-back" className="btn"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
              ) : (
                <button onClick={() => nav(-1)} className="btn">Cancel</button>
              )}
              {step < STEPS.length - 1 ? (
                <button onClick={next} data-testid="cq-next" className="btn btn-primary" style={{background:"#4B9CFF", borderColor:"#4B9CFF"}}>Next <ArrowRight className="h-3.5 w-3.5" /></button>
              ) : (
                <button onClick={submit} disabled={busy} data-testid="cq-submit" className="btn btn-primary" style={{background:"#4B9CFF", borderColor:"#4B9CFF"}}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>REQUEST QUOTES <ArrowRight className="h-3.5 w-3.5" /></>}
                </button>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-4 py-2 border-b border-white/[0.05] last:border-b-0">
      <div className="w-24 eyebrow-muted uppercase">{k}</div>
      <div className="flex-1 text-white/90">{v}</div>
    </div>
  );
}

function SentConfirmation({ result, form, onDone }) {
  return (
    <div className="text-center py-6" data-testid="cq-sent">
      <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
      <div className="overline text-emerald-400 mb-1">REQUEST SENT</div>
      <h3 className="font-display text-2xl font-bold tracking-tight mb-2">
        {result.matched_operators} operator{result.matched_operators === 1 ? "" : "s"} notified.
      </h3>
      {result.is_international && (
        <div className="inline-flex items-center gap-2 mono text-[11px] text-orange-hud bg-orange-hud/[0.10] border border-orange-hud/40 px-3 py-1 rounded-full mb-3">
          <Globe2 className="h-3 w-3" /> INTL · PASSPORT REQUIRED
        </div>
      )}
      <p className="text-sm text-white/60 max-w-md mx-auto mb-6">
        Bids will appear on your request page. You'll also get an email at <span className="mono text-cyan">{form.contact_email}</span> whenever a new bid comes in.
      </p>
      <button onClick={onDone} data-testid="cq-track" className="btn btn-primary" style={{background:"#4B9CFF", borderColor:"#4B9CFF"}}>TRACK BIDS <ArrowRight className="h-3.5 w-3.5" /></button>
    </div>
  );
}


/**
 * DateTimeHourAmPm — mobile-friendly date + hour + AM/PM picker.
 *
 * We ditched the native `datetime-local` control because on iOS the picker
 * defaulted to 5-minute increments and the two side-by-side controls overflowed
 * the viewport. This composed control stacks vertically on mobile, uses a plain
 * <input type="date"> for the calendar, and two small selects for hour + AM/PM.
 * Emits an ISO-ish "YYYY-MM-DDTHH:00" string compatible with the existing form.
 */
function DateTimeHourAmPm({ testId, label, value = "", onChange, minDate }) {
  // value expected: "YYYY-MM-DDTHH:MM"  →  split into date + hour + period.
  const [dateStr = "", timeStr = ""] = (value || "").split("T");
  const parsedHour = parseInt((timeStr || "00").slice(0, 2), 10);
  const isPM = !isNaN(parsedHour) && parsedHour >= 12;
  const hour12 = isNaN(parsedHour)
    ? ""
    : ((parsedHour % 12) === 0 ? 12 : parsedHour % 12);

  const emit = (d, h, p) => {
    if (!d) { onChange(""); return; }
    if (h === "" || h == null) { onChange(""); return; }
    let hh = Number(h);
    if (p === "PM" && hh !== 12) hh += 12;
    if (p === "AM" && hh === 12) hh = 0;
    onChange(`${d}T${String(hh).padStart(2, "0")}:00`);
  };

  return (
    <div>
      <div className="eyebrow-muted mb-1.5">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_88px] gap-2">
        <input
          type="date"
          data-testid={`${testId}-date`}
          className="input"
          value={dateStr}
          min={minDate}
          onChange={(e) => emit(e.target.value, hour12 || 9, isPM ? "PM" : "AM")}
          style={{ fontSize: 16 }}  /* Prevents iOS from zooming on focus */
        />
        <select
          data-testid={`${testId}-hour`}
          className="input"
          value={hour12}
          onChange={(e) => emit(dateStr, e.target.value, isPM ? "PM" : "AM")}
          style={{ fontSize: 16 }}
        >
          <option value="">Hour</option>
          {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
            <option key={h} value={h}>{h}:00</option>
          ))}
        </select>
        <select
          data-testid={`${testId}-period`}
          className="input"
          value={isPM ? "PM" : "AM"}
          onChange={(e) => emit(dateStr, hour12 || 9, e.target.value)}
          style={{ fontSize: 16 }}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}
